import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, ShieldCheck, Eye, UserCog, User as UserIcon, Plus, Pencil, Trash2, X, ChevronDown, Loader2 } from "lucide-react";
import { useUiState } from "@/lib/uiState";

const ROLE_META = {
  superadmin: { label: "Super Admin", icon: ShieldCheck, cls: "bg-amber-100 text-amber-800" },
  admin: { label: "Administrator", icon: Shield, cls: "bg-blue-100 text-blue-800" },
  alumni_manager: { label: "Alumni Manager", icon: UserCog, cls: "bg-indigo-100 text-indigo-800" },
  readonly: { label: "Read Only", icon: Eye, cls: "bg-slate-100 text-slate-700" },
  class_agent: { label: "Class Agent", icon: UserCog, cls: "bg-emerald-100 text-emerald-800" },
  alumnus: { label: "Alumnus", icon: UserIcon, cls: "bg-slate-100 text-slate-600" },
};
const ASSIGNABLE = ["superadmin", "admin", "alumni_manager", "readonly"];

function UserFormModal({ mode, initial, onClose, onSaved }) {
  const editing = mode === "edit";
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [role, setRole] = useState(initial?.role || "admin");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const patch = { name: name.trim(), email: email.trim() };
        if (password) patch.password = password;
        await api.patch(`/users/${initial.id}`, patch);
        toast.success("Account updated");
      } else {
        await api.post("/users", { name: name.trim(), email: email.trim(), role, password });
        toast.success("Administrator added");
      }
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not save account");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" data-testid="user-form-modal" onMouseDown={onClose}>
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-serif text-lg tracking-tight">{editing ? "Edit account" : "Add administrator"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="user-form-close"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium">Full name</label>
            <input data-testid="user-form-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 focus:ring-2 focus:ring-accent focus:outline-none" placeholder="Jane Doe" />
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <input data-testid="user-form-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 focus:ring-2 focus:ring-accent focus:outline-none" placeholder="jane@example.com" />
          </div>
          {!editing && (
            <div>
              <label className="text-sm font-medium">Role</label>
              <select data-testid="user-form-role" value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 focus:ring-2 focus:ring-accent focus:outline-none">
                {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_META[r].label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-sm font-medium">{editing ? "New password" : "Password"}</label>
            <input data-testid="user-form-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required={!editing} minLength={8} className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 focus:ring-2 focus:ring-accent focus:outline-none" placeholder={editing ? "Leave blank to keep current" : "At least 8 characters"} />
            {editing && <p className="text-xs text-muted-foreground mt-1">Leave blank to keep the current password.</p>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-muted">Cancel</button>
            <button type="submit" disabled={saving} data-testid="user-form-submit" className="px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">{saving ? "Saving…" : editing ? "Save changes" : "Add account"}</button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function Users() {
  const { user: me, isSuperadmin, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { mode, initial }
  const [emulatingId, setEmulatingId] = useState(null);
  const [staffOpen, setStaffOpen] = useUiState("ud_users_staff_open", true);
  const [othersOpen, setOthersOpen] = useUiState("ud_users_alumni_open", true);

  const emulate = async (u) => {
    if (!window.confirm(`Emulate ${u.name || u.email}? You'll browse the app exactly as they see it until you exit the preview.`)) return;
    setEmulatingId(u.id);
    try {
      const eu = await impersonateUser(u.id);
      toast.success(`Now viewing as ${eu.name || eu.email}`);
      const staffRole = ["superadmin", "admin", "alumni_manager", "readonly"].includes(eu.role);
      navigate(staffRole ? "/" : (eu.role === "class_agent" ? "/agent" : "/experience"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not emulate this user");
      setEmulatingId(null);
    }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data.users || [])).catch((e) => toast.error(apiError(e.response?.data?.detail) || "Failed to load users")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    if (!window.confirm(`Change ${u.name || u.email} to ${ROLE_META[role]?.label || role}?`)) return;
    try {
      await api.patch(`/users/${u.id}/role`, { role });
      toast.success(`${u.name || u.email} is now ${ROLE_META[role]?.label || role}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not change role"); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete the account for ${u.name || u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Account deleted");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not delete account"); }
  };

  const staff = users.filter((u) => ["superadmin", "admin", "readonly"].includes(u.role));
  const others = users.filter((u) => !["superadmin", "admin", "readonly"].includes(u.role));

  const Row = ({ u }) => {
    const meta = ROLE_META[u.role] || ROLE_META.alumnus;
    const Icon = meta.icon;
    const manageable = ASSIGNABLE.includes(u.role);
    const isSelf = me && (me.id === u.id || (me.email || "").toLowerCase() === (u.email || "").toLowerCase());
    return (
      <div data-testid={`user-row-${u.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
        <div className="h-9 w-9 rounded-full bg-secondary/10 flex items-center justify-center text-sm font-semibold text-secondary shrink-0">{(u.name || u.email || "?").slice(0, 1).toUpperCase()}</div>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{u.name || "—"} {isSelf && <span className="text-[10px] font-mono uppercase text-muted-foreground">(you)</span>}</div>
          <div className="text-sm text-muted-foreground truncate">{u.email}</div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded ${meta.cls} shrink-0`}><Icon className="h-3 w-3" /> {meta.label}</span>
        {isSuperadmin && !isSelf && (
          <button
            data-testid={`user-emulate-${u.id}`}
            title={`Emulate ${u.name || u.email}`}
            disabled={emulatingId === u.id}
            onClick={() => emulate(u)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm text-muted-foreground hover:text-primary hover:border-primary/40 disabled:opacity-50"
          >
            {emulatingId === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Emulate
          </button>
        )}
        {manageable ? (
          <>
            <select
              data-testid={`user-role-select-${u.id}`}
              value={u.role}
              onChange={(e) => changeRole(u, e.target.value)}
              className="shrink-0 text-sm rounded-md border border-border bg-background px-2 py-1.5 focus:ring-2 focus:ring-accent focus:outline-none"
            >
              {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_META[r].label}</option>)}
            </select>
            <button data-testid={`user-edit-${u.id}`} title="Edit account" onClick={() => setModal({ mode: "edit", initial: u })} className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`user-delete-${u.id}`} title="Delete account" disabled={isSelf} onClick={() => removeUser(u)} className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"><Trash2 className="h-4 w-4" /></button>
          </>
        ) : (
          <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">Managed in Directory</span>
        )}
      </div>
    );
  };

  return (
    <div className="fade-up">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Users &amp; Administrators</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Manage who can sign in to the admin console and what they can do. Super Admins can manage users; Administrators can edit all records; Read Only can view but not change.</p>
        </div>
        <button data-testid="add-user-btn" onClick={() => setModal({ mode: "create", initial: null })} className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 shrink-0">
          <Plus className="h-4 w-4" /> Add administrator
        </button>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="users-staff">
            <button data-testid="users-staff-collapse" onClick={() => setStaffOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 border-b border-border font-serif text-lg tracking-tight text-left">
              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${staffOpen ? "" : "-rotate-90"}`} />
              <span>Staff &amp; Administrators</span> <span className="text-sm text-muted-foreground">({staff.length})</span>
            </button>
            {staffOpen && (
            <div className="divide-y divide-border">
              {staff.map((u) => <Row key={u.id} u={u} />)}
              {staff.length === 0 && <div className="px-4 py-8 text-center text-muted-foreground">No staff accounts.</div>}
            </div>
            )}
          </div>

          {others.length > 0 && (
            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="users-alumni">
              <button data-testid="users-alumni-collapse" onClick={() => setOthersOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 border-b border-border font-serif text-lg tracking-tight text-left">
                <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${othersOpen ? "" : "-rotate-90"}`} />
                <span>Alumni &amp; Class Agent logins</span> <span className="text-sm text-muted-foreground">({others.length})</span>
              </button>
              {othersOpen && (
              <div className="divide-y divide-border">
                {others.map((u) => <Row key={u.id} u={u} />)}
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {modal && (
        <UserFormModal
          mode={modal.mode}
          initial={modal.initial}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
